import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../../firebase';

const EmailVerification = () => {
  const { currentUser, verifyUserEmail, sendCustomVerificationEmail, checkEmailVerification } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only handle verification if the modal isn't already handling it
    if (!window.handlingEmailVerification) {
      handleEmailVerification();
    } else {
      // If modal is handling it, just show a loading state and redirect
      setStatus('info');
      setMessage('Email verification in progress...');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
  }, [currentUser]); // Add currentUser as dependency

  const handleEmailVerification = async () => {
    try {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const uid = searchParams.get('uid');

      if (mode === 'verifyEmail' && oobCode) {
        // Handle Firebase Auth email verification
        setStatus('loading');
        setMessage('Verifying your email address...');
        
        await applyActionCode(auth, oobCode);
        console.log('Firebase Auth verification completed');
        
        // Wait a moment for currentUser to update, then update our custom verification status
        setTimeout(async () => {
          try {
            if (currentUser) {
              await verifyUserEmail();
              console.log('Custom verification status updated');
            } else {
              // If no current user, just mark as success since Firebase verification worked
              console.log('No current user, but Firebase verification completed');
            }
            
            setStatus('success');
            setMessage('Your email has been verified successfully! You can now access all features.');
            
            // Redirect to dashboard after 4 seconds
            setTimeout(() => {
              navigate('/');
            }, 4000);
          } catch (verifyError) {
            console.error('Error updating custom verification status:', verifyError);
            // Even if custom update fails, Firebase verification succeeded
            setStatus('success');
            setMessage('Your email has been verified successfully! You can now access all features.');
            setTimeout(() => {
              navigate('/');
            }, 4000);
          }
        }, 1000); // Wait 1 second for auth state to update
        
      } else if (uid && currentUser?.uid === uid) {
        // Handle custom verification link (redirect to verify Firebase Auth)
        setStatus('info');
        setMessage('Please complete your email verification by clicking the verification link we sent to your email.');
      } else {
        setStatus('error');
        setMessage('Invalid verification link. Please try requesting a new verification email.');
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      setStatus('error');
      
      if (error.code === 'auth/invalid-action-code') {
        setMessage('This verification link is invalid or has expired. Please request a new verification email.');
      } else if (error.code === 'auth/expired-action-code') {
        setMessage('This verification link has expired. Please request a new verification email.');
      } else {
        setMessage('Failed to verify email. Please try again or contact support.');
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await sendCustomVerificationEmail(true);
      setMessage('Verification email sent! Please check your inbox.');
      setStatus('info');
    } catch (error) {
      console.error('Error sending verification email:', error);
      setMessage('Failed to send verification email. Please try again.');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    try {
      setLoading(true);
      const result = await checkEmailVerification();
      if (result.isVerified) {
        setStatus('success');
        setMessage('Your email is verified! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setMessage('Your email is not yet verified. Please check your inbox for the verification email.');
        setStatus('info');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setMessage('Failed to check verification status. Please try again.');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'info':
        return 'ℹ️';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#059669';
      case 'error':
        return '#dc2626';
      case 'info':
        return '#0284c7';
      default:
        return '#6b7280';
    }
  };

  return (
    <div style={{
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '20px'
        }}>
          {getStatusIcon()}
        </div>

        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#1f2937',
          marginBottom: '16px'
        }}>
          Email Verification
        </h1>

        <p style={{
          fontSize: '16px',
          color: getStatusColor(),
          lineHeight: '1.6',
          marginBottom: '30px'
        }}>
          {message || 'Verifying your email address...'}
        </p>

        {status === 'success' && (
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #10b981',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#047857', margin: '0 0 8px 0' }}>
              Welcome to Practice Talking! 🎉
            </h3>
            <p style={{ color: '#047857', margin: 0, fontSize: '14px' }}>
              Your email is now verified. You have full access to book sessions, become a provider, and connect with other users.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#dc2626', margin: '0 0 8px 0' }}>
              Verification Failed
            </h3>
            <p style={{ color: '#dc2626', margin: 0, fontSize: '14px' }}>
              Don't worry! You can request a new verification email below.
            </p>
          </div>
        )}

        {(status === 'error' || status === 'info') && currentUser && (
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={handleResendVerification}
              disabled={loading}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                marginRight: '12px'
              }}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </button>

            <button
              onClick={handleCheckStatus}
              disabled={loading}
              style={{
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Checking...' : 'Check Status'}
            </button>
          </div>
        )}

        {!currentUser && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
              You need to be logged in to verify your email.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Go to Login
            </button>
          </div>
        )}

        <div style={{
          borderTop: '1px solid #e5e7eb',
          paddingTop: '20px',
          marginTop: '20px'
        }}>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
            Need help? Contact our support team at{' '}
            <a href="mailto:support@practicetalking.co" style={{ color: '#2563eb' }}>
              support@practicetalking.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;